import React, { useState, useEffect, useRef } from "react";
import { FaBook, FaUser, FaChalkboardTeacher, FaCalendarAlt, FaClipboardList, FaTrash, FaEdit, FaRobot } from "react-icons/fa";
import { toast } from "react-toastify";
import { getRequest, postRequest, putRequest, deleteRequest } from "../../apiWrapper";
import { Link } from "react-router-dom";

const PAGE_SIZE = 30; // 1ページあたりの件数
const SESSION_KEY = "lesson_reports_list_state"; // ストレージ保存用キー

const LessonReportsList = ({
  studentid,
  selectedSubject,
  refresh,
  isAdmin,
  showUnconfirmedOnly,
  showUnSuggestedOnly,
  setRefresh,
  renderActions,
}) => {

  // 初回表示判定フラグ（復元時のページ数を保持するため）
  const isFirstRender = useRef(true);

  // 初回ロード時に sessionStorage から復元用データを読み込む
  const savedStateRef = useRef(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const savedState = savedStateRef.current();

  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [subjectColors, setSubjectColors] = useState({});
  const [allStudents, setAllStudents] = useState([]);
  const [filterStudentName, setFilterStudentName] = useState(() => savedState?.filterStudentName || "");
  const [editingReportId, setEditingReportId] = useState(null);
  const [editingStudentName, setEditingStudentName] = useState("");
  const [editingSubjectReportId, setEditingSubjectReportId] = useState(null);
  const [editingSubject, setEditingSubject] = useState("");
  const [currentPage, setCurrentPage] = useState(() => savedState?.currentPage || 1);
  const [hasNextPage, setHasNextPage] = useState(false);

  // スクロール位置復元済みフラグ
  const [isScrollRestored, setIsScrollRestored] = useState(false);

  // 前回のフィルター条件を保持（意図しないページリセットを防ぐため）
  const prevFiltersRef = useRef({
    filterStudentName,
    showUnconfirmedOnly,
    showUnSuggestedOnly,
  });

  // ページネーション用の参照（API 側でページ管理するので不要な場合は削除可）
  const pagesStackRef = useRef([]);

  // 1. フィルター条件が「実際に変更された時のみ」ページ数を 1 にリセット
  useEffect(() => {
    const prev = prevFiltersRef.current;
    const hasChanged =
      prev.filterStudentName !== filterStudentName ||
      prev.showUnconfirmedOnly !== showUnconfirmedOnly ||
      prev.showUnSuggestedOnly !== showUnSuggestedOnly;

    if (hasChanged) {
      prevFiltersRef.current = {
        filterStudentName,
        showUnconfirmedOnly,
        showUnSuggestedOnly,
      };
      setCurrentPage(1);
    }
  }, [filterStudentName, showUnconfirmedOnly, showUnSuggestedOnly]);

  // 2. 生徒名検索やページ数が変わった時に sessionStorage へ保存
  useEffect(() => {
    try {
      const currentSaved = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({
          ...currentSaved,
          filterStudentName,
          currentPage,
        })
      );
    } catch (e) {
      console.error(e);
    }
  }, [filterStudentName, currentPage]);

  // 3. スクロール位置のリアルタイム保存（復元完了後にのみ保存を実行）
  useEffect(() => {
    const handleScroll = () => {
      if (!isScrollRestored) return; // 復元が完了するまでは上書き保存しない

      try {
        const currentSaved = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
        sessionStorage.setItem(
          SESSION_KEY,
          JSON.stringify({
            ...currentSaved,
            scrollY: window.scrollY,
          })
        );
      } catch (e) {
        console.error(e);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isScrollRestored]);

  // 設定から科目ごとのボーダーカラーを取得
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getRequest("settings/default");
        if (data && data.colors && data.colors.subjectBorders) {
          setSubjectColors(data.colors.subjectBorders);
        }
      } catch (error) {
        console.error("❌ 設定の取得エラー:", error);
      }
    };
    fetchSettings();
  }, []);

  // 全生徒一覧の取得（入力補完用）
  useEffect(() => {
    const fetchAllStudents = async () => {
      try {
        const data = await getRequest("students?excludeWithdrawn=true");
        if (data && data.students) {
          setAllStudents(data.students);
        }
      } catch (error) {
        console.error("生徒一覧の取得エラー:", error);
      }
    };
    fetchAllStudents();
  }, []);

  // フィルター用：指定された生徒名で一致する生徒IDのリストを取得
  const getStudentFilterQuery = async () => {
    if (!filterStudentName) return null;
    // API 側で「生徒名」で検索できる場合は、エンドポイントを用いる方法もありますが、
    // ここでは全生徒一覧(allStudents)からフィルターしています
    const filtered = allStudents.filter(student => student.name === filterStudentName);
    return filtered.map(student => student.id);
  };

  // 授業報告一覧を API 経由で取得
  useEffect(() => {
    // 管理者の場合は全件取得、その他は条件付き取得
    const fetchReports = async () => {
      setLoading(true);
      try {
        let endpoint = "";
        if (isAdmin) {
          // 管理者用：オプションでフィルターとページネーションを利用
          endpoint = `lessons?page=${currentPage}&pageSize=${PAGE_SIZE}&order=desc`;
          if (showUnconfirmedOnly) {
            endpoint += `&unconfirmedOnly=true`;
          }
          if (filterStudentName) {
            endpoint += `&filterName=${encodeURIComponent(filterStudentName)}`;
          }
          if (showUnSuggestedOnly) {
            endpoint += `&aiSuggested=false`;  // false を指定
            console.log("wath");
          }
        } else {
          // 学生別の場合：studentid と selectedSubject で絞り込み
          endpoint = `lessons?studentid=${studentid}&subject=${encodeURIComponent(selectedSubject)}&order=desc&page=${currentPage}&pageSize=${PAGE_SIZE}`;
        }
        const data = await getRequest(endpoint);
        if (data && data.reports) {
          setReports(data.reports);
          setHasNextPage(data.hasNextPage);
        } else {
          setReports([]);
          setHasNextPage(false);
        }
      } catch (error) {
        console.error("過去の授業報告の取得エラー:", error);
      } finally {
        setLoading(false);
      }
    };

    // 管理者の場合は必ず実行、非管理者は studentid と selectedSubject が必要
    if (isAdmin || (studentid && selectedSubject)) {
      fetchReports();
    }
  }, [studentid, selectedSubject, refresh, isAdmin, showUnconfirmedOnly, currentPage, filterStudentName, showUnSuggestedOnly]);

  // 4. データ取得完了後（loading === false）に保存していたスクロール位置までジャンプ
  useEffect(() => {
    if (!loading && !isScrollRestored) {
      const savedY = savedState?.scrollY;
      if (reports.length > 0 && savedY) {
        // DOM描画のタイミングを確保するため少し遅延させてスクロール
        const timer = setTimeout(() => {
          window.scrollTo(0, savedY);
          setIsScrollRestored(true);
        }, 100);
        return () => clearTimeout(timer);
      } else {
        setIsScrollRestored(true);
      }
    }
  }, [loading, reports, isScrollRestored, savedState]);

  // 🔹 授業報告の削除処理（管理者のみ）
  const handleDelete = async (reportId) => {
    if (!isAdmin) return;
    if (!window.confirm("この授業報告を削除しますか？")) return;
    try {
      const response = await deleteRequest(`lessons/${reportId}`);
      if (response.ok) {
        toast.success("授業報告を削除しました！");
        setRefresh(prev => !prev);
      } else {
        toast.error("削除に失敗しました");
      }
    } catch (error) {
      console.error("❌ 授業報告の削除エラー:", error);
      toast.error("削除に失敗しました");
    }
  };

  // 生徒名のインライン編集保存処理（管理者用）
  const handleSaveStudentName = async (reportId) => {
    const selectedStudent = allStudents.find(student => student.name === editingStudentName);
    if (!selectedStudent) {
      toast.error("一致する生徒が見つかりません。");
      return;
    }
    try {
      const response = await putRequest(`lessons/${reportId}`, {
        studentid: selectedStudent.id,
      });
      const result = await response.json();
      if (response.ok) {
        toast.success("生徒の変更が完了しました。");
        setReports(prevReports => prevReports.map(report =>
          report.id === reportId
            ? { ...report, studentName: selectedStudent.name, studentid: selectedStudent.id }
            : report
        ));
        setEditingReportId(null);
        setEditingStudentName("");
      } else {
        toast.error("変更に失敗しました");
      }
    } catch (error) {
      console.error("生徒の変更エラー:", error);
      toast.error("変更に失敗しました");
    }
  };

  // 科目のインライン編集保存処理（管理者用）
  const handleSaveSubject = async (reportId) => {
    if (!editingSubject) {
      toast.error("科目が選択されていません。");
      return;
    }
    try {
      const response = await putRequest(`lessons/${reportId}`, {
        subject: editingSubject,
      });
      const result = await response.json();
      if (response.ok) {
        toast.success("科目の変更が完了しました。");
        setReports(prevReports => prevReports.map(report =>
          report.id === reportId ? { ...report, subject: editingSubject } : report
        ));
        setEditingSubjectReportId(null);
        setEditingSubject("");
      } else {
        toast.error("変更に失敗しました");
      }
    } catch (error) {
      console.error("科目の変更エラー:", error);
      toast.error("変更に失敗しました");
    }
  };

  return (
    <div className="mt-6">
      <h3 className="text-lg font-bold mb-4">
        📖 {isAdmin ? "全授業報告一覧" : "過去の授業報告"}
      </h3>

      {/* 管理者用：生徒名フィルター */}
      {isAdmin && (
        <div className="mb-4">
          <label htmlFor="student-filter" className="mr-2 font-bold">生徒名で絞り込み:</label>
          <input
            id="student-filter"
            type="text"
            list="students-filter-list"
            value={filterStudentName}
            onChange={(e) => { setFilterStudentName(e.target.value); setCurrentPage(1); }}
            className="border rounded p-1"
            placeholder="生徒名を入力"
          />
          <datalist id="students-filter-list">
            {allStudents.map(student => (
              <option key={student.id} value={student.name} />
            ))}
          </datalist>
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">読み込み中...</p>
      ) : reports.length === 0 ? (
        <p className="text-gray-500">該当する授業報告はありません。</p>
      ) : (
        reports.map((report) => (
          <div
            key={report.id}
            className={`p-4 mb-4 rounded-lg shadow-md bg-gray-50 ${isAdmin && !report.adminchecked ? "border-yellow-400 border-2" : ""}`}
            style={{ borderLeft: `8px solid ${subjectColors[report.subject] || "rgb(128,128,128)"}` }}
          >
            <div className="flex justify-between items-center mb-2">
              {isAdmin ? (
                editingSubjectReportId === report.id ? (
                  <div className="flex items-center">
                    <h4 className="text-lg font-semibold mr-2">科目:</h4>
                    <select
                      value={editingSubject}
                      onChange={(e) => setEditingSubject(e.target.value)}
                      className="border rounded p-1"
                    >
                      <option value="">選択してください</option>
                      {Object.keys(subjectColors).map((subject) => (
                        <option key={subject} value={subject}>{subject}</option>
                      ))}
                    </select>
                    <button onClick={() => handleSaveSubject(report.id)} className="ml-2 text-green-600">保存</button>
                    <button onClick={() => { setEditingSubjectReportId(null); setEditingSubject(""); }} className="ml-2 text-red-600">キャンセル</button>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <h4 className="text-lg font-semibold mr-2">科目:</h4>
                    <span>{report.subject}</span>
                    <button onClick={() => { setEditingSubjectReportId(report.id); setEditingSubject(report.subject); }} className="ml-2 text-blue-600">
                      <FaEdit />
                    </button>
                  </div>
                )
              ) : (
                <h4 className="text-lg font-semibold">{report.subject}</h4>
              )}
              <span className="text-sm text-gray-600">{report.timestamp_jst.replace("T", " ").split(".")[0]}</span>
            </div>

            {isAdmin ? (
              editingReportId === report.id ? (
                <div className="flex items-center">
                  <FaUser className="mr-2 text-blue-500" />
                  <span className="font-bold mr-2">生徒:</span>
                  <input
                    type="text"
                    list="students-list"
                    value={editingStudentName}
                    onChange={(e) => setEditingStudentName(e.target.value)}
                    className="border rounded p-1"
                  />
                  <button onClick={() => handleSaveStudentName(report.id)} className="ml-2 text-green-600">保存</button>
                  <button onClick={() => { setEditingReportId(null); setEditingStudentName(""); }} className="ml-2 text-red-600">キャンセル</button>
                </div>
              ) : (
                <div className="flex items-center">
                  <FaUser className="mr-2 text-blue-500" />
                  <span className="font-bold mr-2">生徒:</span>
                  <span>{report.studentName}</span>
                  <button onClick={() => { setEditingReportId(report.id); setEditingStudentName(report.studentName); }} className="ml-2 text-blue-600">
                    <FaEdit />
                  </button>
                </div>
              )
            ) : (
              <p className="flex items-center text-gray-700">
                <FaUser className="mr-2 text-blue-500" />
                <span className="font-bold">生徒:</span> {report.studentName}
              </p>
            )}

            <p className="flex items-center text-gray-700">
              <FaChalkboardTeacher className="mr-2 text-blue-500" />
              <span className="font-bold">担当:</span> {report.teacherName}
            </p>

            <p className="flex items-center text-gray-700">
              <FaRobot className="mr-2 text-green-600" />
              <span className="font-bold">AI提案:</span> {report.ai_suggested ? "✅ 提案済み" : "❌ 未提案"}
            </p>

            {report.unit && (
              <p className="flex items-center text-gray-700">
                <FaBook className="mr-2 text-purple-500" />
                <span className="font-bold">単元:</span> {report.unit.length > 12 ? `${report.unit.slice(0, 12)}...` : report.unit}
              </p>
            )}

            {report.lessonnotes && (
              <p className="flex items-center text-gray-700">
                <FaClipboardList className="mr-2 text-orange-500" />
                <span className="font-bold">授業考察:</span> {report.lessonnotes.length > 12 ? `${report.lessonnotes.slice(0, 12)}...` : report.lessonnotes}
              </p>
            )}

            {isAdmin && (
              <>
                <p className="flex items-center text-gray-700">
                  <FaClipboardList className="mr-2 text-green-500" />
                  <span className="font-bold">確認済み:</span> {report.adminchecked ? "✅" : "❌"}
                </p>

                <div className="mt-2 flex space-x-3">
                  <Link
                    to={`/reports/edit/${report.id}`}
                    className="bg-blue-500 text-white px-3 py-1 rounded-md flex items-center hover:bg-blue-700 transition"
                  >
                    <FaEdit className="mr-1" /> 編集
                  </Link>

                  <button
                    className="bg-red-500 text-white px-3 py-1 rounded-md flex items-center hover:bg-red-700 transition"
                    onClick={() => handleDelete(report.id)}
                  >
                    <FaTrash className="mr-1" /> 削除
                  </button>
                </div>
              </>
            )}
          </div>
        ))
      )}

      {/* ページネーションボタン */}
      {isAdmin && (
        <div className="flex justify-center space-x-4 mt-4">
          <button
            onClick={() => {
              if (currentPage > 1) setCurrentPage(currentPage - 1);
            }}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded disabled:opacity-50"
          >
            前のページ
          </button>
          <button
            onClick={() => {
              if (hasNextPage) setCurrentPage(currentPage + 1);
            }}
            disabled={!hasNextPage}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded disabled:opacity-50"
          >
            次のページ
          </button>
        </div>
      )}

      {/* 生徒入力補完用 datalist */}
      <datalist id="students-list">
        {allStudents.map((student) => (
          <option key={student.id} value={student.name} />
        ))}
      </datalist>
    </div>
  );
};

export default LessonReportsList;
